<template>
  <div
    class="flex-row flex-grow position-relative h-full"
    data-test="producer-editor"
  >
    <div v-if="!isSocketConnected" class="disconnected-overlay" />

    <div class="layer-editor level-01dp">
      <div class="header panel-drag-handle">
        <div class="flex items-center">
          <span class="title">{{ title }}</span>

        </div>
      </div>

      <template v-if="editing && isEditable">
        <marker-editor
          v-if="editing.layout == 'marker'"
          :item="editing"
        />

        <graphic
          v-else-if="editing.layout == 'graphic'"
          :key="editing.timeline_id"
          :editing="editing"
          :item="editing"
          ref="graphicEditor"
        />

        <panel
          v-else-if="editing.layout == 'panel'"
          :editing="editing"
          :item="panel"
          ref="panelEditor"
        />

        <asset-editor
          v-else-if="editing.layout == 'asset'"
          :item="editing"
          :editing="editing"
        />

        <poll-editor
          v-else-if="editing.layout == 'poll'"
          :item="editing"
          :editing="editing"
        />

        <theme
          v-else-if="!['segment', 'graphics'].includes(editing.layout)"
          :item="editing"
          :editing="editing"
        />

        <div
          v-if="editing.notes && editing.layout !== 'marker'"
          class="layer-editor-notes"
        >
          <div class="layer-editor-notes__label">Notes</div>
          <p class="layer-editor-notes__body">{{ editing.notes }}</p>
        </div>

        <div v-if="showFooter" class="footer">
          <button
            v-if="editing._id"
            class="tb-btn tb-btn-text sm medium-em"
            :disabled="!unsaved || !editing._id"
            :class="{ disabled: !unsaved }"
            data-entity="producer-editor-saveGraphic"
            ddata-test="graphics-editor-saveButton"
            @click="saveGraphic"
          >
            Save changes
          </button>

          <button
            class="tb-btn tb-btn-contained sm sample"
            :disabled="disableUpdatePreview"
            data-entity="producer-editor-updatePreview"
            data-test="producer-editor-updatePreview"
            @click="sendUpdate({ preview: true, force: true })"
          >
            Update Preview
          </button>

          <button
            class="tb-btn tb-btn-contained sm alarm"
            :disabled="disableUpdateLive"
            data-entity="producer-editor-updateLive"
            data-test="producer-editor-updateLive"
            @click="sendUpdate({ preview: false, force: true })"
          >
            Update Live
          </button>
        </div>
      </template>

      <template v-if="!editing || !isEditable">
        <div class="droparea">
          <div class="droparea-help-text">Click on a playlist item to start editing</div>
        </div>
      </template>

      <modal
        v-if="assetModalOpen"
        class="asset-uploader-modal"
        @close="toggleAssetModal(false)"
      >
        <template #header>
          <div class="title">Choose media</div>
          <button v-on:click="toggleAssetModal(false)" class="modal-close tb-btn tb-btn-text sm low-em"><span class="fas fa-times"></span></button>
        </template>

        <iframe
          id="asset-uploader"
          :src="assetManagerSrc"
          height="100%"
          width="100%"
          style="width: 100%; height: 100%; border: 0; background: white; display: block;"
        />
      </modal>
    </div>
  </div>
</template>

<script>
import { cloneDeep } from 'lodash-es';

import { mapState } from 'vuex';
import GraphicEditorComponent from '../graphic-editor/GraphicEditor.vue';
import ThemeEditorComponent from '../theme-editor/ThemeEditor.vue';
import PanelEditorComponent from '../panel-editor/PanelEditor.vue';
import AssetEditor from '../asset-editor/AssetEditor.vue';
import PollEditor from '../poll-editor/PollEditor.vue';
import ModalComponent from '../modal/Modal.vue';
import MarkerEditorComponent from './MarkerEditor.vue';
import showBuilderNamingMixin from '../../mixins/show-builder-naming-mixin';

const findNested = (posts, timelineId) => {
  for (let i = 0; i < posts.length; i += 1) {
    const post = posts[i];

    if (post?.timeline_id === timelineId) {
      return post;
    }

    if (post?.posts?.length) {
      const nested = findNested(post.posts, timelineId);

      if (nested) {
        return nested;
      }
    }
  }

  return null;
};

export default {
  name: 'LayerEditor',

  mixins: [showBuilderNamingMixin],

  data() {
    return {
      original: null,
      originalTitle: '',
    };
  },

  components: {
    modal: ModalComponent,
    graphic: GraphicEditorComponent,
    theme: ThemeEditorComponent,
    panel: PanelEditorComponent,
    AssetEditor,
    PollEditor,
    markerEditor: MarkerEditorComponent,
  },

  computed: {
    ...mapState({
      isSocketConnected: (state) => state.clients.isSocketConnected,
      timeline: (state) => state.production.production.timeline,
    }),

    // Parent element if graphic is inside a group
    parent() {
      return this.timeline
        .filter((i) => i.layout === 'graphics')
        .find((i) => (
          i.timeline_id !== this.editing.timeline_id
          && !!i.posts.find((p) => p.timeline_id === this.editing.timeline_id)
        ));
    },

    disableUpdateLive() {
      return !this.isCurrentLayout;
    },

    disableUpdatePreview() {
      return !this.isCurrentSampleLayout;
    },

    currentLayoutIndex() {
      return this.$store.state.production.production.index;
    },

    isCurrentLayout() {
      const id = this.editing.timeline_id;
      const parentId = this.parent?.timeline_id;
      return [id, parentId].includes(this.currentLayoutIndex);
    },

    currentSampleLayoutIndex() {
      return this.$store.state.preview.production.index;
    },

    isCurrentSampleLayout() {
      const id = this.editing.timeline_id;
      const parentId = this.parent?.timeline_id;
      return [id, parentId].includes(this.currentSampleLayoutIndex);
    },

    showFooter() {
      if (!this.editing) {
        return false;
      }

      const hiddenFooterLayouts = ['marker', 'segment', 'graphics'];
      return !hiddenFooterLayouts.includes(this.editing.layout);
    },

    title() {
      if (!this.editing || !this.isEditable) {
        return 'Layer Editor';
      }

      if (this.editing.layout === 'marker') {
        return 'Marker Editor';
      }

      if (this.editing.layout === 'graphic') {
        return 'Graphic Editor';
      }

      if (!this.showNewNaming) {
        return 'Display Editor';
      }

      if (this.editing.layout === 'panel') {
        return 'Classic Graphic Editor';
      }

      if (this.editing.layout === 'poll') {
        return 'Poll Editor';
      }

      if (['theme', 'grid', 'waterfall', 'carousel', 'mural'].includes(this.editing.layout)) {
        return 'Social Editor';
      }

      return 'Item Editor';
    },

    /**
     * Layers like "graphics" don't have anything to edit (atm)
     */
    isEditable() {
      return true;
    },

    editing() {
      const posts = this.$store.state.production.production.timeline;
      const { editing } = this.$store.state.timeline;
      return findNested(posts, editing);
    },

    assetModalOpen() {
      return this.$store.state.graphics.assetModalOpen;
    },

    assetManagerSrc() {
      const assetType = this.$store.state.graphics.assetModalOpenType;
      const { owner } = this.$store.state.production.production;
      return `${process.env.TAGLANDIA_URL}/select-asset?owner=${owner}&filetype=${assetType}`;
    },

    panel() {
      return this.editing._id ? this.$store.state.panels.lookupTable[this.editing._id] : this.editing;
    },

    unsaved() {
      if (!this.editing) {
        return false;
      }

      return (
        this.editing.title !== this.originalTitle
        || JSON.stringify(this.editing.fields) !== JSON.stringify(this.original)
      );
    },
  },
  methods: {
    sendUpdate({ preview, force } = {}) {
      const dispatchType = preview ? 'updatePreviewTimeline' : 'updateTimeline';
      this.$store.dispatch(dispatchType, { force });
    },

    async saveGraphic() {
      if (!this.editing._id) {
        return;
      }

      const graphic = this.$store.state.graphics.lookupTable[this.editing._id];

      graphic.title = this.editing.title;
      graphic.fields = this.editing.fields;

      try {
        await this.$store.dispatch('editGraphic', graphic);
      } catch (err) {
        this.errorToast('There was an error saving your graphic. Please try again.');
        return;
      }

      this.successToast('Graphic saved successfully.');
      this.original = cloneDeep(graphic.fields);
      this.originalTitle = graphic.title;
    },

    toggleAssetModal(bool) {
      this.$store.dispatch('toggleAssetModal', { toggled: bool, type: 'image' });
    },
  },

  watch: {
    editing: {
      immediate: true,
      handler(editing) {
        this.original = cloneDeep(editing?.fields || null);
        this.originalTitle = editing?.title || '';
      },
    },
  },
};
</script>
